import { useState, useEffect } from 'react';
import { repositoryFactory } from '@data/repositories/RepositoryFactory.js';
import { useAuth } from '@context';

/**
 * Hook for managing users
 */
export function useUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const userRepo = repositoryFactory.getUserRepository();

  /**
   * Load all users
   */
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const allUsers = await userRepo.getAllUsers({
        viewerUserId: user?.id,
      });
      setUsers(allUsers);
    } catch (err) {
      setError(err.message);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create a new user
   */
  const createUser = async (userData, options = {}) => {
    try {
      setError(null);
      const newUser = await userRepo.createUser(userData, options);
      setUsers(prev => [...prev, newUser]);
      return newUser;
    } catch (err) {
      setError(err.message);
      console.error('Error creating user:', err);
      throw err;
    }
  };

  /**
   * Update an existing user
   */
  const updateUser = async (userId, updates) => {
    try {
      setError(null);
      const updatedUser = await userRepo.updateUser(userId, updates);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;
    } catch (err) {
      setError(err.message);
      console.error('Error updating user:', err);
      throw err;
    }
  };

  /**
   * Delete a user
   */
  const deleteUser = async (userId) => {
    try {
      setError(null);
      const success = await userRepo.deleteUser(userId);
      if (success) {
        setUsers(prev => prev.filter(u => u.id !== userId));
      }
      return success;
    } catch (err) {
      setError(err.message);
      console.error('Error deleting user:', err);
      throw err;
    }
  };

  /**
   * Enroll user in a course
   */
  const enrollUser = async (userId, courseId, options = {}) => {
    try {
      setError(null);
      const updatedUser = await userRepo.enrollUserInCourse(userId, courseId, options);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;
    } catch (err) {
      setError(err.message);
      console.error('Error enrolling user:', err);
      throw err;
    }
  };

  /**
   * Unenroll user from a course
   */
  const unenrollUser = async (userId, courseId) => {
    try {
      setError(null);
      const updatedUser = await userRepo.unenrollUserFromCourse(userId, courseId);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;
    } catch (err) {
      setError(err.message);
      console.error('Error unenrolling user:', err);
      throw err;
    }
  };

  /**
   * Suspend a user in a specific course
   */
  const suspendUserInCourse = async (userId, courseId) => {
    try {
      setError(null);
      const updatedUser = await userRepo.suspendUserInCourse(userId, courseId);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;
    } catch (err) {
      setError(err.message);
      console.error('Error suspending user in course:', err);
      throw err;
    }
  };

  /**
   * Unsuspend a user in a specific course
   */
  const unsuspendUserInCourse = async (userId, courseId) => {
    try {
      setError(null);
      const updatedUser = await userRepo.unsuspendUserInCourse(userId, courseId);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;
    } catch (err) {
      setError(err.message);
      console.error('Error unsuspending user in course:', err);
      throw err;
    }
  };

  /**
   * Get users enrolled in a specific course
   */
  const getUsersByCourse = async (courseId) => {
    try {
      setError(null);
      return await userRepo.getUsersByCourse(courseId);
    } catch (err) {
      setError(err.message);
      console.error('Error getting users by course:', err);
      throw err;
    }
  };

  /**
   * Set/reset user password
   */
  const setPassword = async (userId, password, options = {}) => {
    try {
      setError(null);
      const updatedUser = await userRepo.setUserPassword(userId, password, options);
      setUsers(prev => prev.map(u => u.id === userId ? updatedUser : u));
      return updatedUser;
    } catch (err) {
      setError(err.message);
      console.error('Error setting password:', err);
      throw err;
    }
  };

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, [user?.id]);

  return {
    users,
    loading,
    error,
    createUser,
    updateUser,
    deleteUser,
    enrollUser,
    unenrollUser,
    suspendUserInCourse,
    unsuspendUserInCourse,
    getUsersByCourse,
    setPassword,
    refresh: loadUsers,
  };
}
